'use client';

import { useCallback, useState } from 'react';
import { BUSINESS_INFORMATION_ROUTE } from '../../../../../../../i18n/pathnames/licenses';
import { LOG_CLIENT_NAME } from '../../../../../../../src/lib/instrumentation/consts';
import { logError } from '../../../../../../actions/logger/logServices';
import { fetchLicensePlan } from '../../../../actions/action.fetchLicensePlan';
import { useLicenseStore } from '../../../licenseStore';
import { LicensePlan } from '../../../types/licenses';
import { BusinessInformation } from '../forms/types/businessInformation';

export type UseLicensePlanCalculatorResult = {
  calculate: () => Promise<void>;
  fetchingData: boolean;
  fetchError?: string;
  clearFetchError: () => void;
  licensePlan?: LicensePlan;
  businessInformation?: BusinessInformation;
  resetLicensePlan: () => void;
};

export const useLicensePlanCalculator = (): UseLicensePlanCalculatorResult => {
  const [fetchingData, setFetchingData] = useState(false);
  const [fetchError, setFetchError] = useState<string | undefined>(undefined);

  const { businessInformation, licensePlan, updateLicensePlan, resetLicensePlan, stepValidity } = useLicenseStore();
  const isValid = stepValidity?.[BUSINESS_INFORMATION_ROUTE] === true;

  const calculate = useCallback(async () => {
    try {
      if (!isValid) {
        useLicenseStore.getState().setShowErrors(true);
        if (typeof window !== 'undefined') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        return;
      }

      setFetchingData(true);
      setFetchError(undefined);

      const response = await fetchLicensePlan(useLicenseStore.getState());

      if (response) updateLicensePlan(response);
      setFetchingData(false);
    } catch (error) {
      setFetchingData(false);
      const message =
        error instanceof Error ? error.message : 'An unknown error occurred while calculating license price.';
      logError(new Error('Error fetching license plan: ' + message), {
        instance: LOG_CLIENT_NAME,
        processName: 'licenses',
        action: 'calculate-plan',
        step: 'fetch',
      });
      setFetchError(message);
    }
  }, [isValid, updateLicensePlan]);

  const clearFetchError = useCallback(() => {
    setFetchError(undefined);
  }, []);

  return { calculate, fetchingData, fetchError, clearFetchError, licensePlan, businessInformation, resetLicensePlan };
};
